import React, { useEffect, useMemo, useState } from 'react';
import { isSameDay } from 'date-fns';
import { FaPlus, FaSortDown } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteFavoriteWeekAction,
  duplicateFavoriteWeekAction,
  getFavoriteWeekShiftsAction,
  getShiftsListAction,
  getWeekOptionsAction,
  patchFavoriteWeekAction,
  postFavoriteWeekAction,
} from '../../actions/shift';
import { postUsersFilteredAction } from '../../actions/users';
import { postContractsFilteredAction } from '../../actions/contract';
import { FavioriteWeek, IShift } from '../../types/shift';
import {
  ILine,
  IPole,
  IPool,
  IStructure,
} from '../../types/structure';
import { IUser } from '../../types/user';
import { buildReferenceWeek } from '../../utils/favoriteWeek';
import DropDown from '../../components/DropDown/DropDown';
import ExportButton from '../../components/ExportButton';
import PlanningRow from '../../components/PlanningRow';
import Tooltip from '../../components/Toolip';
import ModalStandalone, { IModalHandle } from '../../lib/ModalStandalone';
import ConfirmModal from '../../components/ConfirmModal';
import styles from './WeekTemplatesTab.module.scss';
import planningStyles from './AdminPlanning.module.scss';

type WeekTemplatesTabProps = {
  structureId: string;
  structuresList: IStructure[];
  usersList: IUser[];
  pools: IPool[];
  user: IUser;
  isMobile: boolean;
};

const WeekTemplatesTab = ({
  structureId,
  structuresList,
  usersList,
  pools,
  user,
  isMobile,
}: WeekTemplatesTabProps) => {
  const dispatch = useDispatch();
  const {
    favioritesWeeks,
    favoriteWeekShiftsList,
    list,
  } = useSelector((store: any) => store.shiftsReducer);

  const [editingWeek, setEditingWeek] = useState<FavioriteWeek | null>(null);
  const [editorDay, setEditorDay] = useState<string | null>(null);
  const [isEditorWeekView, setIsEditorWeekView] = useState(true);
  const [selectedPoles, setSelectedPoles] = useState<string[]>([]);
  const [newName, setNewName] = useState('');
  const [renameName, setRenameName] = useState('');
  const [renameTarget, setRenameTarget] = useState<FavioriteWeek | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<FavioriteWeek | null>(null);
  const [duplicateTarget, setDuplicateTarget] = useState<FavioriteWeek | null>(null);
  const createModalRef = React.useRef<IModalHandle | null>(null);
  const renameModalRef = React.useRef<IModalHandle | null>(null);
  const deleteModalRef = React.useRef<IModalHandle | null>(null);

  const week = useMemo(() => buildReferenceWeek(), []);
  const referenceWeek = useMemo(() => ({
    startDate: week[0]?.date,
    endDate: week[6]?.date,
  }), [week]);
  const structure = structuresList?.find((s: IStructure) => s._id === structureId);
  const polesList = structure?.poles;
  const poles = polesList?.filter((po: IPole) => {
    if (user?.role === 'Manager') {
      if (user?.poles.find((p: string) => p === po._id)) {
        return po;
      }
      return null;
    }
    return po;
  });

  const filteredPoles = useMemo(() => {
    if (!poles) return poles;
    if (selectedPoles.length === 0) return poles;
    return poles.filter((po: IPole) => po._id && selectedPoles.includes(po._id));
  }, [poles, selectedPoles]);

  const usersSorted = useMemo(() => {
    const users: IUser[] = [];
    if (!filteredPoles || !usersList) {
      return users;
    }
    filteredPoles.forEach((po: IPole) => {
      usersList.forEach((u: IUser) => {
        const userFound = users.find((ur: IUser) => ur._id === u._id);
        const poleFound = u.poles.find((s: string) => s === po._id);
        if (!userFound && poleFound && u.isActive && u.role !== 'Manager' && u.role !== 'Admin' && u.role !== 'Owner') {
          users.push(u);
        }
      });
    });
    return users;
  }, [usersList, filteredPoles]);

  useEffect(() => {
    if (structureId) {
      getWeekOptionsAction(dispatch, structureId);
      getShiftsListAction(dispatch, structureId);
      postUsersFilteredAction(dispatch, { structure: structureId, search: '' });
      postContractsFilteredAction(dispatch, {
        structure: structureId,
        startDate: referenceWeek.startDate,
        endDate: referenceWeek.endDate,
      });
    }
  }, [structureId, dispatch, referenceWeek.endDate, referenceWeek.startDate]);

  useEffect(() => {
    if (editingWeek?._id) {
      setEditorDay(week[0]?.date ?? null);
      setIsEditorWeekView(true);
      setSelectedPoles([]);
      getFavoriteWeekShiftsAction(dispatch, editingWeek._id);
    }
  }, [editingWeek?._id, dispatch, week]);

  function handleEditorDay(dir: 'prev' | 'next') {
    if (!editorDay) return;
    const currentIndex = week.findIndex((d) => d.date === editorDay);
    if (currentIndex < 0) return;
    const nextIndex = dir === 'prev' ? currentIndex - 1 : currentIndex + 1;
    if (nextIndex < 0 || nextIndex >= week.length) return;
    setEditorDay(week[nextIndex].date);
  }

  function handleEnterDayView(date: string) {
    setEditorDay(date);
    setIsEditorWeekView(false);
  }

  async function navigateToPreview() {
    const currentUrl = window.location.origin;
    const token = localStorage.getItem('token');
    if (!editingWeek?._id) {
      return false;
    }
    const query: Record<string, string> = {
      structure: structureId,
      planningView: 'global',
      favoriteWeekId: editingWeek._id,
      startDate: isEditorWeekView
        ? referenceWeek.startDate
        : (editorDay || referenceWeek.startDate),
    };
    if (isEditorWeekView) {
      query.endDate = referenceWeek.endDate;
    }
    if (selectedPoles.length > 0) {
      query.poles = selectedPoles.join(',');
    }
    const searchQuery = `?${new URLSearchParams(query)}`;
    const url = `${currentUrl}/export-preview/${token?.replace('JWT ', '')}/${searchQuery}`;
    window.open(url, '_blank');
    return true;
  }

  function filterEditorShifts(predicate: (shift: IShift) => boolean) {
    return favoriteWeekShiftsList.filter((s: IShift) => {
      if (!predicate(s)) return false;
      if (isEditorWeekView || !editorDay) return true;
      return isSameDay(new Date(s.date), new Date(editorDay));
    });
  }

  async function handleCreate() {
    if (!newName.trim()) return;
    if (duplicateTarget) {
      await duplicateFavoriteWeekAction(dispatch, duplicateTarget._id, {
        name: newName.trim(),
        structure: structureId,
      });
      setDuplicateTarget(null);
      setNewName('');
      createModalRef.current?.close();
      return;
    }
    const created = await postFavoriteWeekAction(dispatch, {
      structure: structureId,
      name: newName.trim(),
    });
    setNewName('');
    createModalRef.current?.close();
    if (created) {
      setEditingWeek(created);
    }
  }

  function openCreateModal() {
    setDuplicateTarget(null);
    setNewName('');
    createModalRef.current?.open();
  }

  function openDuplicateModal(templateWeek: FavioriteWeek) {
    setDuplicateTarget(templateWeek);
    setNewName('');
    createModalRef.current?.open();
  }

  async function handleRename() {
    if (!renameTarget || !renameName.trim()) return;
    await patchFavoriteWeekAction(dispatch, renameTarget._id, { name: renameName.trim() });
    renameModalRef.current?.close();
    setRenameTarget(null);
    setRenameName('');
    getWeekOptionsAction(dispatch, structureId);
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    await deleteFavoriteWeekAction(dispatch, deleteTarget._id);
    deleteModalRef.current?.close();
    if (editingWeek?._id === deleteTarget._id) {
      setEditingWeek(null);
    }
    setDeleteTarget(null);
    getWeekOptionsAction(dispatch, structureId);
  }

  const exportPreviewButton = (
    <Tooltip message={'Voir l\'export'}>
      <ExportButton
        callBack={() => navigateToPreview()}
      />
    </Tooltip>
  );

  if (editingWeek) {
    const selectedDay = editorDay || referenceWeek.startDate;
    const editorFilters = {
      structure: structureId,
      startDate: selectedDay,
      endDate: isEditorWeekView ? referenceWeek.endDate : null,
    };
    const weekProp = isEditorWeekView ? week : [];
    const selectedDayLabel = week.find((d) => d.date === selectedDay)?.weekDay;
    const templateMode = {
      favoriteWeekId: editingWeek._id,
      filters: editorFilters,
      onSelectDay: (date: string) => setEditorDay(date),
      onEnterDayView: handleEnterDayView,
    };

    return (
      <div className={styles.editor}>
        <div className={styles.editorHeader}>
          <div className={styles.editorHeaderLeft}>
            <button
              type="button"
              className={styles.back}
              onClick={() => setEditingWeek(null)}
            >
              ← Retour à la liste
            </button>
            <h3>{editingWeek.name || editingWeek.label}</h3>
          </div>
          <div className={styles.editorHeaderRight}>
            {!isEditorWeekView && selectedDayLabel && (
              <div className={styles.selectDate}>
                <button type="button" onClick={() => handleEditorDay('prev')} aria-label="Jour précédent">
                  <FaSortDown />
                </button>
                <div className={styles.dates}>
                  <h4>{selectedDayLabel}</h4>
                </div>
                <button type="button" onClick={() => handleEditorDay('next')} aria-label="Jour suivant">
                  <FaSortDown />
                </button>
              </div>
            )}
            {poles && !isMobile && (
              <div className={styles.polesSelect}>
                <DropDown
                  onChange={(value: string[]) => setSelectedPoles(value)}
                  selected={selectedPoles}
                  multiLabel={selectedPoles.length > 0 ? 'catégories séléctionnées' : 'Catégories'}
                  isMulti
                  options={[
                    { label: 'Toutes les catégories', value: null },
                    ...poles.map((s: IPole) => ({
                      label: s.name,
                      value: s._id,
                      color: s.color,
                    })),
                  ]}
                />
              </div>
            )}
            <div className={styles.viewSelect}>
              <DropDown
                onChange={(value: boolean) => {
                  setIsEditorWeekView(value);
                  if (!value && !editorDay) {
                    setEditorDay(referenceWeek.startDate);
                  }
                }}
                selected={isEditorWeekView}
                options={[
                  { label: 'Vue jour', value: false },
                  { label: 'Vue semaine', value: true },
                ]}
              />
            </div>
            {!isMobile && favoriteWeekShiftsList.length > 0 && exportPreviewButton}
          </div>
        </div>
        <div className={planningStyles.labels}>
          <div className={planningStyles.row} />
          <div className={planningStyles.row}>
            {!isEditorWeekView && list?.shifts?.map((s: { label: string }) => {
              const label = s.label?.includes(':00')
                && s.label !== '02:00'
                && s.label !== structure?.schedules?.end
                ? s.label
                : null;
              return (
                <div
                  key={s.label}
                  style={{ width: `${100 / list.shifts.length}%` }}
                  className={planningStyles.hour}
                >
                  <p>{label}</p>
                </div>
              );
            })}
            {isEditorWeekView && week.map((s) => (
              <div
                key={s.date}
                style={{ width: `${100 / 7}%` }}
                className={planningStyles.hour}
              >
                <p>{s.weekDay}</p>
              </div>
            ))}
          </div>
        </div>
        <div className={planningStyles.list}>
          <div className={planningStyles.pools}>
            {poles && pools?.map((p: IPool) => (
              <div key={p._id} className={planningStyles.grid}>
                {p.lines.map((line: ILine, index: number) => (
                  <PlanningRow
                    key={line._id}
                    line={line}
                    lines={p.lines}
                    pool={p}
                    name={index === 0 ? p.name : null}
                    isMobile={!!isMobile}
                    week={weekProp}
                    shifts={filterEditorShifts((s: IShift) => s.line === line._id)}
                    templateMode={templateMode}
                  />
                ))}
              </div>
            ))}
          </div>
          <div className={planningStyles.grid}>
            {poles && usersSorted
              .map((u: IUser) => (
                <PlanningRow
                  key={u._id}
                  user={u}
                  isMobile={!!isMobile}
                  week={weekProp}
                  shifts={filterEditorShifts((s: IShift) => s.user === u._id)}
                  templateMode={templateMode}
                />
              ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.listView}>
      <div className={styles.title}>
        <div className={styles.heading}>
          <h1>Modèles de semaines</h1>
          <p>{favioritesWeeks.length}</p>
        </div>
        <button type="button" className={styles.new} onClick={() => openCreateModal()}>
          <div className={styles.icon}>
            <FaPlus />
          </div>
          Nouveau
        </button>
      </div>
      {favioritesWeeks.length === 0
        ? <p className={styles.empty}>Aucun modèle de semaine pour cette structure.</p>
        : <div className={styles.list}>
          <div className={styles.grid}>
            {favioritesWeeks.map((w: FavioriteWeek) => {
              const label = w.name || w.label || '';
              return (
                <div key={w._id} className={styles.card}>
                  <div className={styles.cardTop}>
                    <div className={styles.infos}>
                      <h3>{label}</h3>
                      {w.createdAt && (
                        <h4>
                          {`Créé le ${new Intl.DateTimeFormat('fr', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(w.createdAt))}`}
                        </h4>
                      )}
                    </div>
                  </div>
                  <div className={styles.actions}>
                    <button type="button" onClick={() => setEditingWeek(w)}>Éditer</button>
                    <button
                      type="button"
                      onClick={() => {
                        setRenameTarget(w);
                        setRenameName(label);
                        renameModalRef.current?.open();
                      }}
                    >
                      Renommer
                    </button>
                    <button
                      type="button"
                      onClick={() => openDuplicateModal(w)}
                    >
                      Dupliquer
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setDeleteTarget(w);
                        deleteModalRef.current?.open();
                      }}
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      }

      <ModalStandalone ref={createModalRef} minWidth={450}>
        <div className={styles.modal}>
          <h4>{duplicateTarget ? 'Dupliquer le modèle' : 'Nouveau modèle'}</h4>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Nom du modèle"
          />
          <div className={styles.modalActions}>
            <button
              type="button"
              onClick={() => {
                setDuplicateTarget(null);
                createModalRef.current?.close();
              }}
            >
              Annuler
            </button>
            <button type="button" onClick={() => handleCreate()}>
              {duplicateTarget ? 'Dupliquer' : 'Créer'}
            </button>
          </div>
        </div>
      </ModalStandalone>

      <ModalStandalone ref={renameModalRef} minWidth={450}>
        <div className={styles.modal}>
          <h4>Renommer le modèle</h4>
          <input
            value={renameName}
            onChange={(e) => setRenameName(e.target.value)}
            placeholder="Nom du modèle"
          />
          <div className={styles.modalActions}>
            <button type="button" onClick={() => renameModalRef.current?.close()}>Annuler</button>
            <button type="button" onClick={() => handleRename()}>Enregistrer</button>
          </div>
        </div>
      </ModalStandalone>

      <ModalStandalone ref={deleteModalRef} minWidth={450} isClose={false}>
        <ConfirmModal
          message="Êtes-vous sûr de vouloir supprimer ce modèle ?"
          confirmAction={() => handleDelete()}
          abort={() => deleteModalRef.current?.close()}
        />
      </ModalStandalone>
    </div>
  );
};

export default WeekTemplatesTab;
